// TouchPoint shapes are generated from OrbitalX.Dto — see api-generated.d.ts.
//
// Every enum below is a runtime `enum` rather than a re-exported union, because callers compare
// against its members and index by them. Wire values match the API's EnumExtensions.ToDescription()
// output, which is the codebase-wide convention for enum serialization.

import type {
  EmailSendRuleDto as GeneratedEmailSendRuleDto,
  EmailTouchPointPropertiesDto as GeneratedEmailTouchPointPropertiesDto,
  EmailTouchPointPropertiesViewDto as GeneratedEmailTouchPointPropertiesViewDto,
  MeetingTouchPointPropertiesDto as GeneratedMeetingTouchPointPropertiesDto,
  SubjectSplitTestDto as GeneratedSubjectSplitTestDto,
  TouchPointCreateDto as GeneratedTouchPointCreateDto,
  TouchPointUpdateDto as GeneratedTouchPointUpdateDto,
  TouchPointViewDto as GeneratedTouchPointViewDto,
} from './api-generated'

export type {
  MeetingRecordingUrlDto,
  MeetingTranscriptUrlDto,
  PhoneTouchPointPropertiesDto,
  SocialTouchPointPropertiesDto,
} from './api-generated'

export enum TouchPointType {
  Email = 'Email',
  Phone = 'Phone',
  Social = 'Social',
  Meeting = 'Meeting',
}

// EmailSendRule — structured timing config for email touchpoints.
// Either an absolute send time (Kind=Scheduled) or an event-anchored offset (Kind=Event).

export enum EmailSendRuleKind {
  Scheduled = 'Scheduled',
  Event = 'Event',
}

export enum EmailOffsetUnit {
  Minute = 'Minute',
  Hour = 'Hour',
  Day = 'Day',
}

// Consent class for an email touchpoint (ORB-340). Marketing emails honor recipient
// opt-outs; Transactional emails always send. `null` lets the server smart-default.
export enum EmailComplianceClass {
  Marketing = 'Marketing',
  Transactional = 'Transactional',
}

// Per-touchpoint engagement stage a Download nurture targets (ORB-359). Wire values match the API's
// EnumExtensions.ToDescription() output. None = no engagement gate (the default for non-nurture emails).
export enum EmailEngagementTarget {
  None = 'None',
  Unopened = 'Unopened',
  OpenedNotClicked = 'OpenedNotClicked',
  ClickedNotConverted = 'ClickedNotConverted',
}

// How a Scheduled rule's local wall-clock time maps to UTC (ORB-352).
// FixedUtc: the local time/zone pair resolves to one absolute instant for all recipients.
// RecipientLocal: per-recipient local delivery — UI is a gated fast-follow; included here for type parity.
export enum EmailScheduledSendMode {
  FixedUtc = 'FixedUtc',
  RecipientLocal = 'RecipientLocal',
}

export enum EmailEventType {
  WebRegistered = 'Web Registered',
  OnlineEventStart = 'Online Event Start',
  OnlineEventEnd = 'Online Event End',
  AssetDownloaded = 'Asset Downloaded',
  MeetingBooked = 'Meeting Booked',
  DemoBooked = 'Demo Booked',
  // Post-event nurture anchors (server EmailEventType 6/7). Past-tense, positive offsets only.
  OnlineEventAttended = 'Online Event Attended',
  OnlineEventMissed = 'Online Event Missed',
  // ORB-536. The registrant-scoped pre-event reminder (server EmailEventType 8). Fires on the SAME
  // instant as OnlineEventStart but reaches the inverse audience — only the people who registered,
  // rather than the imported list minus them — so its CTA is "join" and its default class is the
  // opposite. Future-dated like the other event anchors, so negative offsets are allowed.
  OnlineEventStartRegistrants = 'Online Event Start Registrants',
  // ORB-508. The recipient clicked through from one of the web's marketing emails and their browser
  // reached the landing page. Past-tense, positive offsets only. On a Download web this is the only
  // anchor that can express "clicked but has not converted" — Web Registered and Asset Downloaded are
  // the same instant there, so a nudge on either reaches somebody who already has the asset.
  EmailClicked = 'Email Clicked',
}

// Which metric decides the winning subject in an A/B subject split test (ORB-453). Default Opens.
export enum SplitTestWinMetric {
  Opens = 'Opens',
  Clicks = 'Clicks',
}

// Anchors that fire for ONE recipient at the moment the event happened to them (server:
// IEmailRuleCatalogService.IsRecipientAnchored). Mirrored here because two editor affordances depend
// on it, and the server rejects the combinations rather than silently ignoring them (ORB-508):
// the engagement gate is applied only while building an AUDIENCE, so it means nothing on these.
//
// ⚠ OnlineEventStartRegistrants stays OUT despite its registrant-only audience: the worker sweeps
// that audience as a list and writes many rows (server: Path B), so the gate does apply. The server
// agrees — IsRecipientAnchored returns false for it.
const RECIPIENT_ANCHORED_EVENT_TYPES: ReadonlySet<EmailEventType> = new Set([
  EmailEventType.WebRegistered,
  EmailEventType.AssetDownloaded,
  EmailEventType.MeetingBooked,
  EmailEventType.DemoBooked,
  EmailEventType.EmailClicked,
])

/** True when the rule sends to one person at its trigger instant rather than to a list. */
export function isRecipientAnchoredRule(rule: EmailSendRuleDto | null | undefined): boolean {
  if (!rule || rule.Kind !== EmailSendRuleKind.Event || !rule.EventType) return false
  return RECIPIENT_ANCHORED_EVENT_TYPES.has(rule.EventType)
}

/**
 * `SendAtAbsolute` is server-computed from the local-time/zone pair and populated only when
 * Kind=Scheduled — the editor leaves it null. `OffsetValue` is signed (-5 = "5 days before anchor").
 * `ScheduledSendLocalTime` is an ISO local wall-clock with no offset; `ScheduledSendTimeZone` an IANA
 * zone; `ScheduledSendMode` defaults to FixedUtc server-side (ORB-352).
 */
export interface EmailSendRuleDto
  extends Omit<GeneratedEmailSendRuleDto, 'Kind' | 'EventType' | 'OffsetUnit' | 'ScheduledSendMode'> {
  Kind: EmailSendRuleKind
  EventType?: EmailEventType | null
  OffsetUnit?: EmailOffsetUnit | null
  ScheduledSendMode?: EmailScheduledSendMode | null
}

/**
 * A/B subject-line split-test config on a scheduled email touchpoint (ORB-453). Inert unless Enabled,
 * and only offered when the copy has two subjects. TestCohortPercent is 1–50; MeasurementWindowMinutes
 * is required (the UI collects whole hours and sends hours × 60; server floor 60 min).
 */
export interface SubjectSplitTestDto extends Omit<GeneratedSubjectSplitTestDto, 'WinMetric'> {
  WinMetric?: SplitTestWinMetric | null
}

/**
 * Email-specific properties — a reference to an EmailCopy asset plus the structured EmailSendRule
 * that decides when it fires.
 *
 * `FromSendingDomainId` is required at save time (enforced server-side) and selected in the activation
 * editor. `ComplianceClass` (ORB-340) is server-smart-defaulted when null. `EngagementTarget` (ORB-359)
 * null/None means no engagement gate; the rule editor pre-fills the per-email-type default from the
 * EmailRules lookup. `TargetFileImportIds` (ORB-347) null/empty means all of the Web's included file
 * imports. `SubjectSplitTest` (ORB-453) is honored only for scheduled sends whose copy has two subjects.
 */
export interface EmailTouchPointPropertiesDto
  extends Omit<GeneratedEmailTouchPointPropertiesDto, 'Rule' | 'ComplianceClass' | 'EngagementTarget' | 'SubjectSplitTest'> {
  Rule: EmailSendRuleDto
  ComplianceClass?: EmailComplianceClass | null
  EngagementTarget?: EmailEngagementTarget | null
  SubjectSplitTest?: SubjectSplitTestDto | null
}

/**
 * What a read returns. `Armed` (ORB-378) is server-controlled — saving arms it, Start-over disarms it,
 * Reschedule re-arms it — which is why it only exists on the view and not on the create/update body.
 * `EmailAssetVersionNumber` (ORB-303) is too: choosing the email sets it, and signing a newer version off moves it.
 */
export interface EmailTouchPointPropertiesViewDto extends EmailTouchPointPropertiesDto {
  Armed: GeneratedEmailTouchPointPropertiesViewDto['Armed']
  EmailAssetVersionNumber: GeneratedEmailTouchPointPropertiesViewDto['EmailAssetVersionNumber']
}

/**
 * `SendWebster` is settable on create/update and defaults to true server-side, so omitting it opts
 * IN. The remaining Webster fields are server-managed and read-only on the view — sending them on
 * input has no effect, so they are optional here even though the response always carries them.
 * `BotStatus` is a WebsterBotStatus description ("Not Scheduled" | "Scheduled" | "In Call" |
 * "Processing" | "Done" | "Failed" | "Unsupported URL"); `BotStatusSubCode` is recall.ai's failure
 * reason and present only when BotStatus is "Failed"; `TranscriptJsonPath` is a short-lived SAS URL
 * present once processing completes.
 */
export type MeetingTouchPointPropertiesDto =
  Omit<GeneratedMeetingTouchPointPropertiesDto, 'SendWebster' | 'HasRecording'>
  & Partial<Pick<GeneratedMeetingTouchPointPropertiesDto, 'SendWebster' | 'HasRecording'>>

/** `WebId` is absent on a campaign-level touchpoint that is not tied to a specific web. */
export interface TouchPointCreateDto
  extends Omit<GeneratedTouchPointCreateDto, 'EmailProperties' | 'MeetingProperties'> {
  EmailProperties?: EmailTouchPointPropertiesDto | null
  MeetingProperties?: MeetingTouchPointPropertiesDto | null
}

export interface TouchPointViewDto
  extends Omit<GeneratedTouchPointViewDto, 'EmailProperties' | 'MeetingProperties'> {
  EmailProperties?: EmailTouchPointPropertiesViewDto | null
  MeetingProperties?: MeetingTouchPointPropertiesDto | null
}

export interface TouchPointUpdateDto
  extends Omit<GeneratedTouchPointUpdateDto, 'EmailProperties' | 'MeetingProperties'> {
  EmailProperties?: EmailTouchPointPropertiesDto | null
  MeetingProperties?: MeetingTouchPointPropertiesDto | null
}

// Lookup DTOs - now using standardized format
export type { LookupOptionDto } from '@/types/lookups'

export const TOUCHPOINT_TYPE_LABELS: Record<string, string> = {
  Email: 'Email',
  Phone: 'Call',
  Social: 'Social Post',
  Meeting: 'Meeting',
}

export const TOUCHPOINT_TYPE_ICONS: Record<string, string> = {
  Email: '📧',
  Phone: '📞',
  Social: '📱',
  Meeting: '🤝',
}
